/**
 * The caseload (S-A40).
 *
 * Filtering and sorting are SERVER-side and allowlisted. `CaseViewSet.get_queryset`
 * accepts exactly: status (repeatable), stage, priority, service, service_key, client,
 * department, responsible_lawyer (or the literal "me"), team_member, opened_after,
 * opened_before, closed_after, closed_before, plus `search` and `ordering` against
 * their own allowlists. Anything else is a 400, not a silent ignore — so do not add a
 * filter control here without the backend accepting the parameter first.
 *
 * The list is cursor-paginated, which is why there are no page numbers. See
 * `Pagination` in the design system for why inventing them would be a lie.
 */

import { qk, usePaginatedList, type CaseListItem } from '@legalcrm/api';
import {
  Badge,
  Button,
  Input,
  Select,
  StatusPill,
  Table,
  statusTone,
  type Column,
  type SortState,
} from '@legalcrm/ui';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';
import { Can } from '@/auth/permissions';
import { formatDate, humanise } from '@/lib/format';
import { PageHeader } from '@/shell/PageHeader';

/** Mirrors `Case.Status` in `domains/cases/models.py`. */
const STATUS_OPTIONS = [
  { value: '', label: 'Any status' },
  { value: 'open', label: 'Open' },
  { value: 'awaiting_client', label: 'Awaiting client' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'Any priority' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];

const MINE_OPTIONS = [
  { value: '', label: 'Everyone' },
  { value: 'me', label: 'Mine' },
];

const PRIORITY_TONES = {
  urgent: 'danger',
  high: 'warning',
  normal: 'neutral',
  low: 'neutral',
} as const;

export function CaseListPage() {
  const navigate = useNavigate();
  const { matterLabel } = useAuth();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [responsible, setResponsible] = useState('');
  const [sort, setSort] = useState<SortState>({ field: 'opened_at', direction: 'desc' });

  // Debounced: the search filter runs server-side against a scoped queryset, and a
  // request per keystroke is a request per keystroke against the whole caseload.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const list = usePaginatedList<CaseListItem>(qk.list('cases'), '/cases', {
    search: debouncedSearch || undefined,
    status: status || undefined,
    priority: priority || undefined,
    responsible_lawyer: responsible || undefined,
    ordering: `${sort.direction === 'desc' ? '-' : ''}${sort.field}`,
  });

  // A changed filter applied at an old cursor shows the wrong slice of the new
  // result set, so the cursor resets whenever the query changes.
  const { resetCursor } = list;
  useEffect(() => {
    resetCursor();
  }, [debouncedSearch, status, priority, responsible, sort, resetCursor]);

  const columns: Column<CaseListItem>[] = [
    {
      key: 'case_number',
      header: 'Reference',
      sortable: true,
      width: 'w-36',
      render: (row) => (
        <Link
          to={`/cases/${row.id}`}
          className="font-medium text-primary hover:underline"
          // The row is clickable, but a real link is what gives middle-click,
          // "open in new tab" and a status-bar URL.
          onClick={(event) => event.stopPropagation()}
        >
          {row.case_number}
        </Link>
      ),
    },
    {
      key: 'title',
      header: 'Title',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-ink">{row.title}</p>
          <p className="truncate text-xs text-ink-muted">{row.client_name}</p>
        </div>
      ),
    },
    {
      key: 'stage',
      header: 'Stage',
      hideOnMobile: true,
      // The firm's configured stage NAME, never the key. The key is internal
      // vocabulary and means nothing to the person reading the screen.
      render: (row) => <Badge tone="info">{row.stage_name}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      hideOnMobile: true,
      render: (row) => (
        <StatusPill label={humanise(row.status)} tone={statusTone(row.status)} />
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      hideOnMobile: true,
      render: (row) => (
        <Badge tone={PRIORITY_TONES[row.priority] ?? 'neutral'} size="sm">
          {humanise(row.priority)}
        </Badge>
      ),
    },
    {
      key: 'responsible_lawyer',
      header: 'Responsible',
      hideOnMobile: true,
      render: (row) =>
        row.responsible_lawyer ? (
          <span className="text-ink">{row.responsible_lawyer.name}</span>
        ) : (
          // An unassigned matter is a failure state, not a blank.
          <Badge tone="warning" size="sm">
            Unassigned
          </Badge>
        ),
    },
    {
      key: 'opened_at',
      header: 'Opened',
      sortable: true,
      align: 'right',
      hideOnMobile: true,
      render: (row) => <span className="tabular-nums">{formatDate(row.opened_at)}</span>,
    },
  ];

  return (
    <>
      <PageHeader
        title={`${matterLabel}s`}
        description="Every matter you are permitted to see."
        actions={
          <Can module="case" action="create">
            <Button variant="primary" onClick={() => navigate('/cases/new')}>
              New {matterLabel.toLowerCase()}
            </Button>
          </Can>
        }
      />

      <div className="mb-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <Input
          label="Search"
          hideLabel
          type="search"
          placeholder="Search reference or title"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Select
          label="Status"
          hideLabel
          options={STATUS_OPTIONS}
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        />
        <Select
          label="Priority"
          hideLabel
          options={PRIORITY_OPTIONS}
          value={priority}
          onChange={(event) => setPriority(event.target.value)}
        />
        <Select
          label="Responsible lawyer"
          hideLabel
          options={MINE_OPTIONS}
          value={responsible}
          onChange={(event) => setResponsible(event.target.value)}
        />
      </div>

      <Table
        caption={`${matterLabel} list`}
        columns={columns}
        rows={list.items}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        sort={sort}
        onSortChange={setSort}
        onRowClick={(row) => navigate(`/cases/${row.id}`)}
        emptyTitle={`No ${matterLabel.toLowerCase()}s to show`}
        emptyDescription="Nothing matches these filters."
        pagination={{
          hasNext: list.hasNext,
          hasPrevious: list.hasPrevious,
          onNext: list.goNext,
          onPrevious: list.goPrevious,
          summary: `${list.items.length} shown`,
        }}
      />
    </>
  );
}
